import {
  Alert,
  Button,
  Code,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import {
  useClaimHumanDecision,
  useHumanDecisions,
  useReleaseHumanDecisionClaim,
  useResolveHumanDecision,
  useTerminalizeHumanDecision,
} from '@/hooks/api/runs/useHumanDecisions.ts';
import { HumanDecisionRequest } from '@/types/runs.ts';

type Props = { runId: string; decisionId?: string };

const HumanDecisionPanel = ({ runId, decisionId }: Props) => {
  const { data: decisions = [], isLoading, error } = useHumanDecisions(runId);
  if (error) {
    return (
      <Alert color="yellow" title="Human decision">
        Unable to load the decision request.
      </Alert>
    );
  }
  if (isLoading) return null;
  const visibleDecisions = decisionId
    ? decisions.filter((decision) => decision.id === decisionId)
    : decisions;
  if (!visibleDecisions.length) return null;

  return (
    <Stack gap="sm">
      {visibleDecisions.map((decision) => (
        <DecisionForm key={decision.id} decision={decision} runId={runId} />
      ))}
    </Stack>
  );
};

const DecisionForm = ({ decision, runId }: { decision: HumanDecisionRequest; runId: string }) => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [reason, setReason] = useState('');
  const resolve = useResolveHumanDecision();
  const terminalize = useTerminalizeHumanDecision();
  const claim = useClaimHumanDecision();
  const releaseClaim = useReleaseHumanDecisionClaim();
  const summary =
    typeof decision.request_payload.summary === 'string'
      ? decision.request_payload.summary
      : 'A decision is required.';
  const isOpen = decision.status === 'pending' || decision.status === 'claimed';
  const isTestResponse = decision.resolution_provenance?.kind === 'automated_test';
  const canAct = decision.can_resolve || decision.can_terminalize;

  const setValue = (fieldId: string, next: unknown) => {
    setValues((current) => {
      const updated = { ...current };
      if (next === undefined || next === null || next === '') {
        delete updated[fieldId];
      } else {
        updated[fieldId] = next;
      }
      return updated;
    });
  };

  const validatedResponsePayload = (): Record<string, unknown> | null => {
    const missingFields = decision.response_fields
      .filter((field) => {
        const value = values[field.field_id];
        return field.required && (value === undefined || value === null || value === '');
      })
      .map((field) => field.label);
    if (missingFields.length) {
      notifications.show({
        color: 'yellow',
        message: `Complete required response fields: ${missingFields.join(', ')}.`,
      });
      return null;
    }
    if (decision.require_reason && !reason.trim()) {
      notifications.show({
        color: 'yellow',
        message: 'A reason is required before submitting this decision.',
      });
      return null;
    }
    return Object.fromEntries(
      Object.entries(values).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submit = async () => {
    const responsePayload = validatedResponsePayload();
    if (responsePayload === null) return;
    try {
      await resolve.mutateAsync({
        decisionId: decision.id,
        runId,
        payload: {
          response_payload: responsePayload,
          response_reason: reason,
          expected_revision: decision.revision,
        },
      });
      notifications.show({ color: 'green', message: 'Decision submitted. Continuing the run.' });
    } catch {
      notifications.show({
        color: 'red',
        message: 'The decision could not be submitted. Refresh and try again.',
      });
    }
  };

  const terminalizeDecision = async (action: 'reject' | 'cancel') => {
    if (!reason.trim()) {
      notifications.show({
        color: 'yellow',
        message: 'A reason is required to close this decision.',
      });
      return;
    }
    try {
      await terminalize.mutateAsync({
        decisionId: decision.id,
        runId,
        action,
        reason,
        expectedRevision: decision.revision,
      });
      notifications.show({ color: 'green', message: `Decision ${action}ed.` });
    } catch {
      notifications.show({
        color: 'red',
        message: 'The decision could not be closed. Refresh and try again.',
      });
    }
  };

  const updateClaim = async (action: 'claim' | 'release') => {
    try {
      const mutation = action === 'claim' ? claim : releaseClaim;
      await mutation.mutateAsync({ decisionId: decision.id, runId });
      notifications.show({
        color: 'green',
        message: action === 'claim' ? 'Review claimed.' : 'Review claim released.',
      });
    } catch {
      notifications.show({
        color: 'red',
        message: 'The review claim could not be updated. Refresh and try again.',
      });
    }
  };

  return (
    <Alert
      color={isOpen ? 'blue' : 'gray'}
      variant="light"
      title={
        isTestResponse
          ? 'Automated test response'
          : isOpen
            ? 'Human decision required'
            : `Decision: ${decision.status}`
      }
    >
      <Stack gap="sm">
        <Text size="sm">{summary}</Text>
        {decision.claimed_by && <Text size="xs">Claimed by {decision.claimed_by}</Text>}
        <Text size="xs">Evidence snapshot (redacted)</Text>
        <Code block>{JSON.stringify(decision.evidence_snapshot, null, 2)}</Code>
        {decision.can_resolve &&
          decision.response_fields.map((field) => {
            const value = values[field.field_id];
            if (field.field_type === 'enum') {
              return (
                <Select
                  key={field.field_id}
                  label={field.label}
                  required={field.required}
                  data={field.enum_values}
                  value={typeof value === 'string' ? value : null}
                  onChange={(next) => setValue(field.field_id, next)}
                />
              );
            }
            if (field.field_type === 'boolean') {
              return (
                <Select
                  key={field.field_id}
                  label={field.label}
                  required={field.required}
                  placeholder="Select an answer"
                  data={[
                    { value: 'true', label: 'True' },
                    { value: 'false', label: 'False' },
                  ]}
                  value={typeof value === 'boolean' ? String(value) : null}
                  onChange={(next) =>
                    setValue(field.field_id, next === null ? undefined : next === 'true')
                  }
                />
              );
            }
            if (field.field_type === 'number') {
              return (
                <NumberInput
                  key={field.field_id}
                  label={field.label}
                  required={field.required}
                  value={typeof value === 'number' ? value : ''}
                  onChange={(next) =>
                    setValue(field.field_id, typeof next === 'number' ? next : undefined)
                  }
                />
              );
            }
            return (
              <TextInput
                key={field.field_id}
                label={field.label}
                required={field.required}
                type={field.field_type === 'date' ? 'date' : 'text'}
                maxLength={field.max_length ?? undefined}
                value={typeof value === 'string' ? value : ''}
                onChange={(event) => setValue(field.field_id, event.currentTarget.value)}
              />
            );
          })}
        {canAct && (
          <Textarea
            label="Reason"
            required={decision.require_reason}
            placeholder="Record why this is the correct decision, or why it should be closed."
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
        )}
        {isOpen && (decision.can_claim || decision.can_release_claim || canAct) && (
          <Group justify="flex-end">
            {(decision.can_claim || decision.can_release_claim) && (
              <Button
                variant="subtle"
                onClick={() => updateClaim(decision.can_release_claim ? 'release' : 'claim')}
                loading={claim.isPending || releaseClaim.isPending}
              >
                {decision.can_release_claim ? 'Release claim' : 'Claim review'}
              </Button>
            )}
            {decision.can_terminalize && (
              <>
                <Button
                  variant="subtle"
                  color="red"
                  onClick={() => terminalizeDecision('cancel')}
                  loading={terminalize.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="subtle"
                  color="orange"
                  onClick={() => terminalizeDecision('reject')}
                  loading={terminalize.isPending}
                >
                  Reject
                </Button>
              </>
            )}
            {decision.can_resolve && (
              <Button onClick={submit} loading={resolve.isPending}>
                Submit decision
              </Button>
            )}
          </Group>
        )}
        {isOpen && !canAct && !decision.can_claim && !decision.can_release_claim && (
          <Text size="xs" c="dimmed">
            This decision is claimed by another reviewer or you do not have permission to act on it.
          </Text>
        )}
        {!isOpen && (
          <>
            <Text size="sm">
              {isTestResponse
                ? 'Automated test response — not human approval.'
                : decision.resolution_provenance?.kind === 'human'
                  ? 'Submitted by an authenticated human reviewer.'
                  : 'Reviewer provenance was not recorded.'}
            </Text>
            <Text size="sm">{decision.response_reason || 'No reason recorded.'}</Text>
          </>
        )}
      </Stack>
    </Alert>
  );
};

export default HumanDecisionPanel;
